import { useMemo } from 'react';

import {
  isGlobalErrorPageStatus,
  isInlineContainerErrorStatus,
  normalizeContainerErrorStatus,
} from './normalize-container-error-status';

type TInlineStatus = 403 | 404 | 500;

interface IResolveContainerErrorStateParams {
  errors: unknown[];
  isLoading: boolean;
  hasData: boolean;
}

interface IResolvedContainerErrorState {
  shouldShowErrorScreen: boolean;
  inlineStatus: TInlineStatus | null;
}

const INLINE_FALLBACK_STATUS: TInlineStatus = 500;

export function resolveContainerErrorState(
  params: IResolveContainerErrorStateParams,
): IResolvedContainerErrorState {
  const { errors, isLoading, hasData } = params;
  if (isLoading || hasData || errors.length === 0) {
    return {
      shouldShowErrorScreen: false,
      inlineStatus: null,
    };
  }

  const statuses = errors
    .map((error) => normalizeContainerErrorStatus(error))
    .filter((status): status is number => status != null);

  if (statuses.length === 0) {
    return {
      shouldShowErrorScreen: true,
      inlineStatus: INLINE_FALLBACK_STATUS,
    };
  }

  if (statuses.some((status) => isGlobalErrorPageStatus(status))) {
    return {
      shouldShowErrorScreen: false,
      inlineStatus: null,
    };
  }

  const inlineStatus = statuses.find((status): status is TInlineStatus =>
    isInlineContainerErrorStatus(status),
  );

  if (inlineStatus != null) {
    return {
      shouldShowErrorScreen: true,
      inlineStatus,
    };
  }

  return {
    shouldShowErrorScreen: true,
    inlineStatus: INLINE_FALLBACK_STATUS,
  };
}

export function useResolvedContainerErrorState(
  params: IResolveContainerErrorStateParams,
): IResolvedContainerErrorState {
  return useMemo(
    () => resolveContainerErrorState(params),
    [params.errors, params.hasData, params.isLoading],
  );
}
